import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronUp, ChevronDown, Send, Trash2, AlertTriangle, Mic, MicOff, Lock, Sparkles, ArrowRight, Search, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import MarkdownMessage, { parseTicketActions, TicketActionCard, PrefUpdateCard } from '../components/MarkdownMessage';
import SEO from '../components/SEO';
import { BetaBadge } from '../components/PageShell';
import { useSubscription } from '../contexts/SubscriptionContext';
import { authFetch, API } from '../utils/api';


/* ── Date helpers for iMessage-style timestamps ── */
function formatDateSeparator(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  if (isToday) return 'Today';
  if (isYesterday) return 'Yesterday';
  const diff = (now - d) / (1000 * 60 * 60 * 24);
  if (diff < 7) return d.toLocaleDateString('en-US', { weekday: 'long' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
}

function formatTime(dateStr) {
  return new Date(dateStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function shouldShowDateSeparator(messages, idx) {
  if (idx === 0) return true;
  const prev = new Date(messages[idx - 1].created_at).toDateString();
  const curr = new Date(messages[idx].created_at).toDateString();
  return prev !== curr;
}

function shouldShowTime(messages, idx) {
  if (idx === 0) return true;
  const prev = new Date(messages[idx - 1].created_at);
  const curr = new Date(messages[idx].created_at);
  return (curr - prev) > 5 * 60 * 1000; // 5 min gap
}

/* ── Delete Confirmation Modal — simplified with countdown ── */
function DeleteChatModal({ onConfirm, onCancel }) {
  const [countdown, setCountdown] = useState(null);

  useEffect(() => {
    if (countdown === null) return;
    if (countdown <= 0) { onConfirm(); return; }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, onConfirm]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center px-4"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-sm bg-bg-paper border border-white/10 rounded-2xl p-5 shadow-2xl"
        data-testid="delete-chat-modal"
      >
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-full bg-red-500/15 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-4 h-4 text-red-400" />
          </div>
          <div>
            <p className="text-sm font-semibold">Delete chat history?</p>
            <p className="text-[11px] text-muted-foreground">This permanently erases all messages</p>
          </div>
        </div>

        {countdown !== null ? (
          <div className="text-center py-2 mb-3">
            <p className="text-xs text-muted-foreground mb-2">Deleting in...</p>
            <p className="text-3xl font-bold text-red-400 font-mono">{countdown}</p>
            <button
              data-testid="delete-chat-undo-btn"
              onClick={() => { setCountdown(null); onCancel(); }}
              className="mt-3 text-xs text-primary font-bold hover:underline"
            >
              Cancel — keep my chat
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <button
              data-testid="delete-chat-cancel-btn"
              onClick={onCancel}
              className="flex-1 py-2.5 rounded-xl text-xs font-medium bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
            >
              Keep it
            </button>
            <button
              data-testid="delete-chat-confirm-btn"
              onClick={() => setCountdown(5)}
              className="flex-1 py-2.5 rounded-xl text-xs font-bold bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors"
            >
              Delete
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

export default function Chat({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isPro } = useSubscription();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [ticketCreated, setTicketCreated] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [typingMsgIdx, setTypingMsgIdx] = useState(null);
  const [revealedLen, setRevealedLen] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchActiveIdx, setSearchActiveIdx] = useState(0);
  const [credits, setCredits] = useState(null); // { used, limit }
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const searchInputRef = useRef(null);
  const msgRefs = useRef({});
  const recognitionRef = useRef(null);
  const baseTextRef = useRef('');
  const prefillHandled = useRef(false);
  const typingTimer = useRef(null);

  // Speech-to-text setup
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.onresult = (event) => {
      let final = '';
      let interim = '';
      for (let i = 0; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) { final += t; } else { interim += t; }
      }
      const base = baseTextRef.current;
      const spoken = (final + interim).trim();
      setInput(base ? `${base} ${spoken}` : spoken);
    };
    recognition.onend = () => { setIsListening(false); };
    recognition.onerror = () => { setIsListening(false); };
    recognitionRef.current = recognition;
    return () => { try { recognition.stop(); } catch {} };
  }, []);

  const toggleListening = () => {
    if (!recognitionRef.current) return;
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      baseTextRef.current = input.trim();
      try { recognitionRef.current.start(); setIsListening(true); } catch {}
    }
  };

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, sending, revealedLen, scrollToBottom]);

  // Typing animation
  useEffect(() => {
    if (typingMsgIdx === null) return;
    const msg = messages[typingMsgIdx];
    if (!msg) { setTypingMsgIdx(null); return; }
    const { cleanContent } = parseTicketActions(msg.content);
    const fullLen = cleanContent.length;
    if (revealedLen >= fullLen) {
      clearTimeout(typingTimer.current);
      setTypingMsgIdx(null);
      return;
    }
    const ch = cleanContent[revealedLen];
    const speed = ch === ' ' || ch === '\n' ? 5 : ch === '.' || ch === ',' || ch === '!' || ch === '?' ? 30 : 12;
    typingTimer.current = setTimeout(() => {
      setRevealedLen(prev => Math.min(prev + 1, fullLen));
    }, speed);
    return () => clearTimeout(typingTimer.current);
  }, [typingMsgIdx, revealedLen, messages]);

  useEffect(() => {
    const loadHistory = async () => {
      try {
        const res = await authFetch(`${API}/api/chat/history`);
        if (res.ok) {
          const data = await res.json();
          setMessages(data.messages || []);
          setHasMore(data.has_more || false);
          setNextCursor(data.next_cursor || null);
        }
      } catch {}
    };
    loadHistory();
  }, []);

  const loadOlderMessages = useCallback(async () => {
    if (!hasMore || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await authFetch(`${API}/api/chat/history?before=${encodeURIComponent(nextCursor)}`);
      if (res.ok) {
        const data = await res.json();
        const older = data.messages || [];
        if (older.length > 0) {
          setMessages(prev => [...older, ...prev]);
        }
        setHasMore(data.has_more || false);
        setNextCursor(data.next_cursor || null);
      }
    } catch {} finally { setLoadingMore(false); }
  }, [hasMore, nextCursor, loadingMore]);

  // Load daily credits
  const loadCredits = useCallback(async () => {
    if (isPro) { setCredits(null); return; }
    try {
      const [usageRes, statusRes] = await Promise.all([
        authFetch(`${API}/api/billing/usage`),
        authFetch(`${API}/api/billing/status`),
      ]);
      if (usageRes.ok && statusRes.ok) {
        const usage = await usageRes.json();
        const status = await statusRes.json();
        const limit = status.gate_config?.free_daily_messages || 13;
        setCredits({ used: usage.daily_messages || 0, limit });
      }
    } catch {}
  }, [isPro]);

  useEffect(() => { loadCredits(); }, [loadCredits]);

  // Handle prefilled message from navigation
  useEffect(() => {
    if (prefillHandled.current) return;
    const prefill = location.state?.prefillMessage;
    if (prefill) {
      prefillHandled.current = true;
      setInput(prefill);
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const handleSend = async (overrideText) => {
    const text = (overrideText || input).trim();
    if (!text || sending) return;
    setInput('');
    setSending(true);
    const userMsg = { role: 'user', content: text, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    try {
      const res = await authFetch(`${API}/api/chat/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message: text }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.gate) {
          const gateMsg = { role: 'assistant', content: data.message, gate: data.gate, created_at: new Date().toISOString() };
          setMessages(prev => [...prev, gateMsg]);
        } else {
          const newMsg = { role: 'assistant', content: data.content, created_at: new Date().toISOString() };
          setMessages(prev => {
            const updated = [...prev, newMsg];
            setTypingMsgIdx(updated.length - 1);
            setRevealedLen(0);
            return updated;
          });
        }
        loadCredits();
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "Connection dropped - mind trying again?", created_at: new Date().toISOString() }]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleCreateTicket = async (subject, description) => {
    let category = 'general';
    const catMatch = description.match(/Category:\s*(bug|billing|account|feature_request|general)/i);
    if (catMatch) category = catMatch[1].toLowerCase();
    const cleanDesc = description.replace(/Category:\s*\w+\.?\s*/i, '').trim();
    try {
      const res = await authFetch(`${API}/api/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ subject, description: cleanDesc, category }),
      });
      if (res.ok) {
        const ticket = await res.json();
        setTicketCreated(ticket.ticket_id);
        setMessages(prev => [...prev, { role: 'assistant', content: `ticket **${ticket.ticket_id}** has been created! you can track it in **Profile > My Tickets**.`, created_at: new Date().toISOString() }]);
        setTimeout(() => setTicketCreated(null), 5000);
      }
    } catch {}
  };

  const handleUpdateTicket = async (ticketId, comment) => {
    try {
      const res = await authFetch(`${API}/api/tickets/${ticketId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ comment }),
      });
      if (res.ok) {
        setMessages(prev => [...prev, { role: 'assistant', content: `done — **${ticketId}** has been updated with your comment.`, created_at: new Date().toISOString() }]);
      }
    } catch {}
  };

  const handleCloseTicket = async (ticketId, reason) => {
    try {
      const res = await authFetch(`${API}/api/tickets/${ticketId}/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reason }),
      });
      if (res.ok) {
        setMessages(prev => [...prev, { role: 'assistant', content: `**${ticketId}** is now closed. if anything comes up again, just let me know.`, created_at: new Date().toISOString() }]);
      }
    } catch {}
  };

  const handleUpdatePref = async (field, value) => {
    try {
      let body = {};
      if (field === 'preferred_wave_types' || field === 'favorite_spots') {
        body[field] = value.split(',').map(v => v.trim()).filter(Boolean);
      } else {
        body[field] = value;
      }
      await authFetch(`${API}/api/preferences/quick`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
      });
    } catch {}
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  // Auto-resize textarea
  const handleInputChange = (e) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  };

  const handleClear = async () => {
    try {
      await authFetch(`${API}/api/chat/history`, { method: 'DELETE',  });
      setMessages([]);
    } catch {}
    setShowDeleteModal(false);
  };

  // Search filtering
  const searchLower = searchQuery.toLowerCase();
  const matchedList = useMemo(() => {
    if (!searchLower) return [];
    const list = [];
    messages.forEach((m, i) => { if (m.content.toLowerCase().includes(searchLower)) list.push(i); });
    return list;
  }, [messages, searchLower]);
  const matchedSet = useMemo(() => new Set(matchedList), [matchedList]);
  const activeMatchMsgIdx = matchedList.length > 0 ? matchedList[searchActiveIdx] : null;

  // Reset active index when query changes
  useEffect(() => { setSearchActiveIdx(0); }, [searchQuery]);

  // Scroll to active match
  useEffect(() => {
    if (activeMatchMsgIdx !== null && msgRefs.current[activeMatchMsgIdx]) {
      msgRefs.current[activeMatchMsgIdx].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [activeMatchMsgIdx]);

  const searchPrev = () => setSearchActiveIdx(i => (i - 1 + matchedList.length) % matchedList.length);
  const searchNext = () => setSearchActiveIdx(i => (i + 1) % matchedList.length);

  const remaining = credits ? Math.max(0, credits.limit - credits.used) : null;

  return (
    <div className="min-h-screen flex items-start justify-center px-0 py-0 md:px-4 md:py-12 relative z-10">
      <SEO title="Chat with Swell" description="Chat with Swell, your AI surf coach. Get personalized tips, session analysis, and surf forecasts." path="/chat" />
      <div className="w-full max-w-none md:max-w-md bg-bg-paper/95 backdrop-blur-xl rounded-none md:rounded-3xl border-0 md:border md:border-white/10 md:shadow-2xl overflow-hidden flex flex-col chat-container" data-testid="chat-page">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
          <button data-testid="chat-back-btn" onClick={() => navigate('/home')} className="p-1.5 hover:bg-white/10 rounded-full transition-colors">
            <ChevronLeft className="w-5 h-5 text-muted-foreground" />
          </button>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full overflow-hidden border border-white/10">
              <img src="https://images.unsplash.com/photo-1543002601-b85cd5b8439b?w=80&h=80&fit=crop" alt="Swell" className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="text-sm font-semibold leading-tight">Swell</p>
              <p className="text-[10px] text-muted-foreground">your surf coach</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button data-testid="chat-search-btn" onClick={() => { setSearchOpen(!searchOpen); setSearchQuery(''); }} className="p-1.5 hover:bg-white/10 rounded-full transition-colors" title="Search chat">
              <Search className="w-4 h-4 text-muted-foreground" />
            </button>
            <button data-testid="chat-clear-btn" onClick={() => setShowDeleteModal(true)} className="p-1.5 hover:bg-white/10 rounded-full transition-colors" title="Clear chat">
              <Trash2 className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <AnimatePresence>
          {searchOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="overflow-hidden border-b border-white/5"
            >
              <div className="px-4 py-2 flex items-center gap-2">
                <Search className="w-3.5 h-3.5 text-muted-foreground/50 flex-shrink-0" />
                <input
                  ref={searchInputRef}
                  data-testid="chat-search-input"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.shiftKey ? searchPrev() : searchNext(); } }}
                  placeholder="Search messages..."
                  className="flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground/30"
                  autoFocus
                />
                {searchQuery && matchedList.length > 0 && (
                  <>
                    <span className="text-[10px] text-muted-foreground/50 flex-shrink-0 tabular-nums">
                      {searchActiveIdx + 1} / {matchedList.length}
                    </span>
                    <button data-testid="chat-search-prev" onClick={searchPrev} className="p-0.5 hover:bg-white/10 rounded transition-colors">
                      <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                    <button data-testid="chat-search-next" onClick={searchNext} className="p-0.5 hover:bg-white/10 rounded transition-colors">
                      <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                  </>
                )}
                {searchQuery && matchedList.length === 0 && (
                  <span className="text-[10px] text-muted-foreground/30 flex-shrink-0">no results</span>
                )}
                <button onClick={() => { setSearchOpen(false); setSearchQuery(''); }} className="p-1 hover:bg-white/10 rounded-full transition-colors">
                  <X className="w-3 h-3 text-muted-foreground" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1" data-testid="chat-messages">
          {/* Load older messages */}
          {hasMore && (
            <div className="flex justify-center pb-2">
              <button
                data-testid="load-older-btn"
                onClick={loadOlderMessages}
                disabled={loadingMore}
                className="text-[10px] text-primary/70 hover:text-primary font-medium px-3 py-1.5 rounded-full bg-primary/5 border border-primary/10 hover:bg-primary/10 transition-colors disabled:opacity-40"
              >
                {loadingMore ? 'Loading...' : 'Load older messages'}
              </button>
            </div>
          )}

          {messages.length === 0 && !sending && (
            <div className="flex flex-col items-center justify-center h-full text-center px-4" data-testid="chat-empty-state">
              <div className="w-14 h-14 rounded-full overflow-hidden border border-white/10 mb-3">
                <img src="https://images.unsplash.com/photo-1543002601-b85cd5b8439b?w=160&h=160&fit=crop" alt="Swell" className="w-full h-full object-cover" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-1">hey, {user?.name?.split(' ')[0] || 'friend'}</h3>
              <p className="text-muted-foreground text-xs leading-relaxed max-w-xs">
                ask me about conditions, technique, gear, or just chat about waves
              </p>
            </div>
          )}

          {messages.flatMap((msg, i) => {
            const items = [];
            const isSearchHit = searchLower && matchedSet.has(i);
            const isActiveHit = i === activeMatchMsgIdx;
            const isDimmed = searchLower && !isSearchHit;

            // Date separator
            if (shouldShowDateSeparator(messages, i)) {
              items.push(
                <div key={`date-${i}`} className="flex items-center justify-center py-3">
                  <span className="text-[10px] text-muted-foreground/40 font-medium bg-bg-paper/80 px-3 py-0.5 rounded-full border border-white/[0.04]">
                    {formatDateSeparator(msg.created_at)} {formatTime(msg.created_at)}
                  </span>
                </div>
              );
            } else if (shouldShowTime(messages, i)) {
              // Time-only separator for 5+ min gaps within same day
              items.push(
                <div key={`time-${i}`} className="flex justify-center py-1.5">
                  <span className="text-[9px] text-muted-foreground/30">{formatTime(msg.created_at)}</span>
                </div>
              );
            }

            if (msg.role === 'user') {
              items.push(
                <motion.div key={`u-${i}`} ref={el => { msgRefs.current[i] = el; }} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}
                  className={`flex justify-end transition-opacity duration-200 ${isDimmed ? 'opacity-20' : ''}`}>
                  <div data-testid={`chat-message-user-${i}`} className={`max-w-[85%] px-3.5 py-2.5 text-[13px] leading-relaxed bg-primary text-black rounded-2xl rounded-tr-md whitespace-pre-wrap ${isActiveHit ? 'ring-2 ring-yellow-400 shadow-lg shadow-yellow-400/20' : isSearchHit ? 'ring-1 ring-yellow-400/40' : ''}`}>
                    {msg.content}
                  </div>
                </motion.div>
              );
              return items;
            }

            // Assistant messages
            const { cleanContent, actions } = parseTicketActions(msg.content);
            const isTyping = typingMsgIdx === i;
            const displayContent = isTyping ? cleanContent.slice(0, revealedLen) : cleanContent;

            // Gate message
            if (msg.gate) {
              items.push(
                <motion.div key={`gate-${i}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex justify-start">
                  <div className="max-w-[85%] bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-2xl rounded-tl-md px-4 py-3.5">
                    <div className="flex items-center gap-2 mb-2">
                      <Lock className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">Daily Limit Reached</span>
                    </div>
                    <p className="text-[13px] text-white/80 leading-relaxed mb-3">{msg.content}</p>
                    <button
                      data-testid="chat-upgrade-btn"
                      onClick={() => navigate('/billing')}
                      className="flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-4 py-2 rounded-xl hover:brightness-110 transition-all"
                    >
                      <Sparkles className="w-3 h-3" /> Go Pro <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                </motion.div>
              );
              return items;
            }

            if (cleanContent) {
              items.push(
                <motion.div key={`a-${i}`} ref={el => { msgRefs.current[i] = el; }} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}
                  className={`flex justify-start transition-opacity duration-200 ${isDimmed ? 'opacity-20' : ''}`}>
                  <div data-testid={`chat-message-assistant-${i}`} className={`max-w-[85%] px-3.5 py-2.5 text-[13px] leading-relaxed bg-white/5 text-white rounded-2xl rounded-tl-md border border-white/5 ${isActiveHit ? 'ring-2 ring-yellow-400 shadow-lg shadow-yellow-400/20' : isSearchHit ? 'ring-1 ring-yellow-400/40' : ''}`}>
                    <MarkdownMessage content={displayContent} />
                    {isTyping && <span className="inline-block w-0.5 h-3.5 bg-primary/70 ml-0.5 animate-pulse align-text-bottom" />}
                  </div>
                </motion.div>
              );
            }

            // Action cards after typing done
            if (!isTyping) {
              actions.forEach((action, j) => {
                if (action.type === 'pref_update') {
                  items.push(
                    <motion.div key={`a-${i}-p-${j}`} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.25, delay: 0.15 }} className="flex justify-start">
                      <div className="w-[85%]"><PrefUpdateCard action={action} onUpdatePref={handleUpdatePref} /></div>
                    </motion.div>
                  );
                } else if (action.type === 'buttons') {
                  items.push(
                    <motion.div key={`a-${i}-b-${j}`} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, delay: 0.1 }} className="flex flex-wrap gap-1.5 pl-1">
                      {action.labels.map((label, k) => (
                        <button key={k} data-testid={`quick-reply-${k}`} onClick={() => { setInput(label); setTimeout(() => handleSend(label), 50); }}
                          className="text-[11px] px-3 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors font-medium">
                          {label}
                        </button>
                      ))}
                    </motion.div>
                  );
                } else {
                  items.push(
                    <motion.div key={`a-${i}-t-${j}`} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.25, delay: 0.15 }} className="flex justify-start">
                      <div className="w-[85%]"><TicketActionCard action={action} onCreateTicket={handleCreateTicket} onUpdateTicket={handleUpdateTicket} onCloseTicket={handleCloseTicket} /></div>
                    </motion.div>
                  );
                }
              });
            }
            return items;
          })}

          {sending && (
            <div className="flex justify-start">
              <div className="bg-white/5 rounded-2xl rounded-tl-md border border-white/5 px-4 py-3">
                <div className="flex space-x-1.5">
                  <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full typing-dot"></div>
                  <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full typing-dot"></div>
                  <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full typing-dot"></div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-white/5 p-3">
          <div className="flex items-end gap-2">
            <div className="flex-1 bg-white/5 border border-white/10 rounded-2xl px-3.5 py-2.5">
              <textarea
                data-testid="chat-input"
                ref={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="message swell..."
                rows={1}
                className="w-full bg-transparent outline-none text-[13px] resize-none placeholder:text-muted-foreground/40 overflow-y-auto"
                style={{ minHeight: '20px', maxHeight: '120px' }}
              />
            </div>
            {recognitionRef.current && isPro && (
              <button
                data-testid="chat-mic-btn"
                onClick={toggleListening}
                className={`p-2.5 rounded-full flex-shrink-0 transition-all ${isListening ? 'bg-red-500 text-white animate-pulse shadow-lg shadow-red-500/30' : 'bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-white'}`}
                title={isListening ? 'Stop listening' : 'Voice input'}
              >
                {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
            )}
            <button
              data-testid="chat-send-btn"
              onClick={() => handleSend()}
              disabled={!input.trim() || sending}
              className="p-2.5 bg-primary text-black rounded-full hover:opacity-90 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>

          {/* Daily Credits */}
          {remaining !== null && (
            <div className="flex items-center justify-center gap-1.5 mt-2" data-testid="chat-credits">
              <div className="flex gap-0.5">
                {Array.from({ length: credits.limit }, (_, idx) => (
                  <div key={idx} className={`w-1 h-1 rounded-full ${idx < remaining ? 'bg-primary/60' : 'bg-white/10'}`} />
                ))}
              </div>
              <span className="text-[9px] text-muted-foreground/40">{remaining} message{remaining !== 1 ? 's' : ''} left today</span>
            </div>
          )}
        </div>
        <BetaBadge />
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <DeleteChatModal
            onConfirm={handleClear}
            onCancel={() => setShowDeleteModal(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
